<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { setStoreLogger } from '@/logger'
import { PwsLeadGetList, PwsLeadNoteGet, PwsLeadNoteSave } from '@/api/Lead'
import type { ILeadItem } from '@/types/LeadTypes'
import { SetIsLoading } from '@/services/AoeHelpers'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { TextArea as KTextArea } from '@progress/kendo-vue-inputs'
import { GetHostBenefitsUrl } from '@/stores/SettingsStore'

const { t } = useI18n()
const logger = useLogger()
setStoreLogger(logger)

interface LeadTask {
  id: string
  name: string
  completed: boolean
  dueDate: string
  showCoaching: boolean
}

interface LeadWithTasks extends ILeadItem {
  tasks: LeadTask[]
  hasChanges?: boolean
  noteText?: string
  showNoteInput?: boolean
  noteLoading?: boolean
}

const acceptedLeads = ref<LeadWithTasks[]>([])
const isLoading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')
const hostBenefitsUrl = ref('')

function createDefaultTasks(acceptedDate: string, leadGuid: string): LeadTask[] {
  // Calculate due date: one month after accepted date
  const accepted = new Date(acceptedDate)
  const dueDate = new Date(accepted)
  dueDate.setMonth(dueDate.getMonth() + 1)
  const dueDateString = dueDate.toISOString()

  return [
    { id: 'contact', name: t('eventTasks.taskNames.contact'), completed: getTaskCompletionFromStorage(leadGuid, 'task1'), dueDate: dueDateString, showCoaching: false },
    { id: 'email', name: t('eventTasks.taskNames.email'), completed: getTaskCompletionFromStorage(leadGuid, 'task2'), dueDate: dueDateString, showCoaching: false },
    { id: 'followup', name: t('eventTasks.taskNames.followup'), completed: getTaskCompletionFromStorage(leadGuid, 'task3'), dueDate: dueDateString, showCoaching: false }
  ]
}

function getTaskCompletionFromStorage(leadGuid: string, taskNumber: string): boolean {
  const key = `${leadGuid}_${taskNumber}`
  const stored = localStorage.getItem(key)
  return stored === 'true'
}

function saveTaskCompletionToStorage(leadGuid: string, taskNumber: string, completed: boolean) {
  const key = `${leadGuid}_${taskNumber}`
  localStorage.setItem(key, completed.toString())
  logger.debug(`Saved task completion to localStorage: ${key} = ${completed}`)
}

logger.debug('AoeEventTasks.vue')

onMounted(async () => {
  hostBenefitsUrl.value = await GetHostBenefitsUrl()
  await loadAcceptedLeads()
})

async function loadAcceptedLeads() {
  try {
    isLoading.value = true
    SetIsLoading(true)
    errorMessage.value = ''

    logger.debug('Fetching leads...')
    const leadsResponse = await PwsLeadGetList()

    logger.debug('Leads response:', leadsResponse)

    if (!leadsResponse.items || leadsResponse.items.length === 0) {
      logger.info('No leads found')
      return
    }

    // Filter for leads with non-null acceptedDate
    const filtered = leadsResponse.items.filter(lead => lead.acceptedDate !== null && lead.acceptedDate !== undefined && lead.acceptedDate !== '')

    // Add tasks to each lead
    acceptedLeads.value = filtered.map(lead => ({
      ...lead,
      tasks: createDefaultTasks(lead.acceptedDate, lead.sfaLeadMstGuid),
      hasChanges: false,
      noteText: '',
      showNoteInput: false,
      noteLoading: false
    }))

    logger.debug('Accepted leads loaded:', acceptedLeads.value.length)

  } catch (error) {
    logger.error('Error loading leads:', error)
    errorMessage.value = t('eventTasks.failedToLoadLeads')
  } finally {
    isLoading.value = false
    SetIsLoading(false)
  }
}

function formatDate(dateString: string): string {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString()
}

function onTaskChange(lead: LeadWithTasks, taskId: string) {
  const task = lead.tasks.find(t => t.id === taskId)
  if (task) {
    lead.hasChanges = true
    
    // Map task id to task number for localStorage
    const taskNumberMap: Record<string, string> = {
      'contact': 'task1',
      'email': 'task2',
      'followup': 'task3'
    }
    const taskNumber = taskNumberMap[taskId]
    
    // Save to localStorage immediately
    if (taskNumber) {
      saveTaskCompletionToStorage(lead.sfaLeadMstGuid, taskNumber, task.completed)
    }
    
    logger.debug(`Task ${taskId} (${taskNumber}) changed for lead ${lead.sfaLeadMstGuid}: ${task.completed}`)
  }
}

async function saveTasks(lead: LeadWithTasks) {
  try {
    SetIsLoading(true)
    successMessage.value = ''
    errorMessage.value = ''

    logger.debug(`Saving tasks for lead ${lead.sfaLeadMstGuid}:`, lead.tasks)

    // Task completion is already saved to localStorage in toggleTask
    // This function is kept for future API integration
    
    // TODO: Call API to save tasks to backend when ready
    await new Promise(resolve => setTimeout(resolve, 500))

    lead.hasChanges = false
    successMessage.value = t('eventTasks.tasksSavedSuccessfullyFor', {
      firstName: lead.firstName,
      lastName: lead.lastName,
    })

    // Clear success message after 3 seconds
    setTimeout(() => {
      successMessage.value = ''
    }, 3000)

  } catch (error) {
    logger.error('Error saving tasks:', error)
    errorMessage.value = t('eventTasks.failedToSaveTasks')
  } finally {
    SetIsLoading(false)
  }
}

function toggleCoaching(lead: LeadWithTasks, taskId: string) {
  const task = lead.tasks.find(t => t.id === taskId)
  if (task) {
    task.showCoaching = !task.showCoaching
    logger.debug(`Coaching toggled for task ${taskId}:`, task.showCoaching)
  }
}

function getCoachingText(taskId: string): string {
  switch (taskId) {
    case 'contact':
      return t('eventTasks.coaching.contact')
    case 'email': {
      // Get the base URL (protocol + host + port)
      const baseUrl = `${window.location.protocol}//${window.location.host}`
      const fullUrl = hostBenefitsUrl.value.startsWith('http') 
        ? hostBenefitsUrl.value 
        : `${baseUrl}${hostBenefitsUrl.value}`
      return t('eventTasks.coaching.email', { fullUrl })
    }
    case 'followup':
      return t('eventTasks.coaching.followup')
    default:
      return ''
  }
}

async function toggleNoteInput(lead: LeadWithTasks) {
  lead.showNoteInput = !lead.showNoteInput
  
  // If opening the note input and note hasn't been loaded yet, fetch it
  if (lead.showNoteInput && !lead.noteText && !lead.noteLoading) {
    await loadLeadNote(lead)
  }
}

async function loadLeadNote(lead: LeadWithTasks) {
  try {
    lead.noteLoading = true
    logger.debug('Loading note for lead:', lead.sfaLeadMstGuid)
    
    const response = await PwsLeadNoteGet(lead.sfaLeadMstGuid)
    logger.debug('Note response:', response)
    
    if (response && response.item && response.item.noteText) {
      lead.noteText = response.item.noteText
    } else {
      lead.noteText = ''
    }
  } catch (error) {
    logger.error('Error loading note:', error)
    lead.noteText = ''
    errorMessage.value = t('eventTasks.errorLoadingNote')
  } finally {
    lead.noteLoading = false
  }
}

async function saveLeadNote(lead: LeadWithTasks) {
  try {
    SetIsLoading(true)
    logger.debug('Saving note for lead:', lead.sfaLeadMstGuid, lead.noteText)
    
    await PwsLeadNoteSave(lead.sfaLeadMstGuid, lead.noteText || '')
    
    successMessage.value = t('eventTasks.noteSavedSuccessfullyFor', {
      firstName: lead.firstName,
      lastName: lead.lastName,
    })
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      successMessage.value = ''
    }, 3000)
    
  } catch (error) {
    logger.error('Error saving note:', error)
    errorMessage.value = t('eventTasks.failedToSaveNote')
  } finally {
    SetIsLoading(false)
  }
}

function getFullJoinTeamUrl(): string {
  // Get the base URL (protocol + host + port)
  const baseUrl = `${window.location.protocol}//${window.location.host}`
  const fullUrl = hostBenefitsUrl.value.startsWith('http') 
    ? hostBenefitsUrl.value 
    : `${baseUrl}${hostBenefitsUrl.value}`
  return fullUrl
}

async function copyJoinTeamUrl() {
  try {
    const fullUrl = getFullJoinTeamUrl()
    
    // Copy to clipboard
    await navigator.clipboard.writeText(fullUrl)
    
    successMessage.value = t('eventTasks.joinMyTeamUrlCopied')
    logger.debug('Copied URL to clipboard:', fullUrl)
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      successMessage.value = ''
    }, 3000)
    
  } catch (error) {
    logger.error('Error copying to clipboard:', error)
    errorMessage.value = t('eventTasks.failedToCopyUrl')
  }
}
</script>

<template>
  <div class="aoe-event-tasks">
    <div class="event-tasks-header">
      <h2>{{ t('eventTasks.title') }}</h2>
      <p>{{ t('eventTasks.subtitle') }}</p>
    </div>

    <div v-if="errorMessage" class="alert alert-danger">
      {{ errorMessage }}
    </div>

    <div v-if="successMessage" class="alert alert-success">
      {{ successMessage }}
    </div>

    <div v-if="isLoading" class="loading-message">
      {{ t('eventTasks.loading') }}
    </div>

    <div v-else-if="acceptedLeads.length === 0" class="no-events-message">
      {{ t('eventTasks.noLeads') }}
    </div>

    <div v-else class="leads-list">
      <div
        v-for="lead in acceptedLeads"
        :key="lead.sfaLeadMstGuid"
        class="lead-card"
      >
        <div class="lead-header">
          <h3 class="lead-name">{{ lead.firstName }} {{ lead.lastName }}</h3>
          <div v-if="lead.codeIdLeadStatus" class="lead-status">
            {{ lead.codeIdLeadStatus.localizedValue }}
          </div>
        </div>

        <div class="lead-details">
          <div class="lead-info">
            <div class="info-item">
              <strong>{{ t('eventTasks.email') }}:</strong>
              {{ lead.emailAddress }}
            </div>
            <div v-if="lead.primaryPhone" class="info-item">
              <strong>{{ t('eventTasks.phone') }}:</strong>
              {{ lead.primaryPhone }}
            </div>
            <div class="info-item">
              <strong>{{ t('eventTasks.leadType') }}:</strong>
              {{ lead.sfaLeadTypeId }}
            </div>
            <div v-if="lead.codeIdLeadSource && lead.codeIdLeadSource.localizedValue" class="info-item">
              <strong>{{ t('eventTasks.source') }}:</strong>
              {{ lead.codeIdLeadSource.localizedValue }}
            </div>
          </div>

          <div class="lead-note-section">
            <div class="note-header">
              <span 
                class="note-label clickable"
                @click="toggleNoteInput(lead)"
              >
                <strong>{{ t('eventTasks.note') }}</strong>
              </span>
            </div>
            <div v-if="lead.showNoteInput" class="note-input-container">
              <div v-if="lead.noteLoading" class="note-loading">
                {{ t('eventTasks.loadingNote') }}
              </div>
              <div v-else>
                <KTextArea
                  v-model="lead.noteText"
                  :rows="4"
                  :placeholder="t('eventTasks.notePlaceholder')"
                  class="note-textarea"
                />
                <div class="note-actions">
                  <KButton
                    :theme-color="'primary'"
                    @click="saveLeadNote(lead)"
                    class="save-note-button"
                  >
                    {{ t('eventTasks.saveNote') }}
                  </KButton>
                </div>
              </div>
            </div>
          </div>

          <div class="lead-dates">
            <div class="date-item">
              <strong>{{ t('eventTasks.created') }}:</strong>
              {{ formatDate(lead.createDate) }}
            </div>
            <div class="date-item">
              <strong>{{ t('eventTasks.assigned') }}:</strong>
              {{ formatDate(lead.assignedDate) }}
            </div>
            <div class="date-item">
              <strong>{{ t('eventTasks.accepted') }}:</strong>
              {{ formatDate(lead.acceptedDate) }}
            </div>
            <div v-if="lead.partyDate" class="date-item">
              <strong>{{ t('eventTasks.partyDate') }}:</strong>
              {{ formatDate(lead.partyDate) }}
            </div>
          </div>

          <div v-if="lead.codeIdLeadTheme && lead.codeIdLeadTheme.localizedValue" class="lead-theme">
            <strong>{{ t('eventTasks.theme') }}:</strong>
            {{ lead.codeIdLeadTheme.localizedValue }}
          </div>

          <div v-if="lead.codeIdLeadBestTime && lead.codeIdLeadBestTime.localizedValue" class="lead-best-time">
            <strong>{{ t('eventTasks.bestTime') }}:</strong>
            {{ lead.codeIdLeadBestTime.localizedValue }}
          </div>
        </div>

        <div class="lead-tasks">
          <h4 class="tasks-header">{{ t('eventTasks.tasks') }}</h4>
          <div class="tasks-list">
            <div
              v-for="task in lead.tasks"
              :key="task.id"
              class="task-item"
              :class="{ 'task-completed': task.completed }"
            >
              <label class="task-label">
                <input
                  type="checkbox"
                  v-model="task.completed"
                  @change="onTaskChange(lead, task.id)"
                  class="k-checkbox task-checkbox"
                />
                <div class="task-info">
                  <span 
                    class="task-name clickable"
                    @click.prevent="toggleCoaching(lead, task.id)"
                  >
                    {{ task.name }}
                  </span>
                  <span class="task-due-date">{{ t('eventTasks.due') }}: {{ formatDate(task.dueDate) }}</span>
                  <div v-if="task.showCoaching" class="coaching-text-inline">
                    {{ getCoachingText(task.id) }}
                  </div>
                </div>
              </label>
            </div>
          </div>
          <div class="tasks-actions">
            <a 
              :href="getFullJoinTeamUrl()" 
              target="_blank"
              class="register-member-link"
            >
              <KButton
                :theme-color="'secondary'"
                class="register-member-button"
              >
                {{ t('eventTasks.registerNewMember') }}
              </KButton>
            </a>
            <KButton
              :theme-color="'secondary'"
              @click="copyJoinTeamUrl"
              class="join-team-button"
            >
              {{ t('eventTasks.joinMyTeam') }}
            </KButton>
            <KButton
              :theme-color="'primary'"
              @click="saveTasks(lead)"
              :disabled="!lead.hasChanges"
              class="save-tasks-button"
            >
              {{ t('eventTasks.saveTasks') }}
            </KButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.aoe-event-tasks {
  padding: $card-padding;
  max-width: 1200px;
  margin: 0 auto;
}

.event-tasks-header {
  margin-bottom: 30px;
  text-align: center;
}

.event-tasks-header h2 {
  @include typography-h2;
  color: #333;
  margin-bottom: 0.5rem;
}

.event-tasks-header p {
  @include typography-body-large;
  color: #666;
}

.alert {
  padding: $spacing-4;
  margin-bottom: 20px;
  border-radius: $radius-sm;
}

.alert-danger {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}

.alert-success {
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
}

.loading-message,
.no-events-message {
  @include typography-h4;
  text-align: center;
  padding: $spacing-10;
  color: #666;
}

.leads-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: $spacing-5;
}

.lead-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: $radius-md;
  padding: $card-padding;
  box-shadow: 0 2px 4px rgba(black, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
}

.lead-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(black, 0.2);
}

.lead-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid var(--vt-c-white-soft);
  padding-bottom: 15px;
  margin-bottom: 15px;
}

.lead-name {
  @include typography-h4;
  color: #2c3e50;
  margin: 0;
}

.lead-status {
  background: #007bff;
  color: white;
  padding: $spacing-1 $spacing-3;
  @include typography-small;
  @include font-weight-medium;
  border-radius: $radius-2xl;
}

.lead-details {
  display: flex;
  flex-direction: column;
  gap: $spacing-4;
}

.lead-info,
.lead-dates {
  display: flex;
  flex-direction: column;
  gap: $gap-sm;
}

.info-item,
.date-item {
  @include typography-body;
  color: #555;
}

.lead-theme,
.lead-best-time {
  @include typography-body;
  color: #555;
  padding-top: 10px;
  border-top: 1px solid var(--vt-c-white-soft);
}

.lead-tasks {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 2px solid var(--vt-c-white-soft);
}

.tasks-header {
  @include typography-body-large;
  @include font-weight-semibold;
  color: #2c3e50;
  margin-bottom: 12px;
}

.tasks-list {
  display: flex;
  flex-direction: column;
  gap: $spacing-2-5;
  margin-bottom: 15px;
}

.task-item {
  transition: background-color 0.2s;
  padding: $spacing-2;
  border-radius: $radius-sm;
}

.task-item:hover {
  background-color: var(--vt-c-white-soft);
}

.task-completed {
  opacity: 0.6;
}

.task-label {
  display: flex;
  align-items: flex-start;
  cursor: pointer;
  @include typography-body;
  gap: $spacing-2-5;
}

.task-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
  flex-shrink: 0;
  margin-top: 2px;
}

.task-info {
  display: flex;
  flex-direction: column;
  gap: $spacing-1;
  flex: 1;
}

.task-name {
  color: #555;
  line-height: 1.4;
}

.task-name.clickable {
  cursor: pointer;
}

.task-name.clickable:hover {
  text-decoration: underline;
  color: #007bff;
}

.task-due-date {
  @include typography-small;
  color: #888;
  font-style: italic;
}

.task-completed .task-name {
  text-decoration: line-through;
  color: #999999;
}

.task-completed .task-due-date {
  text-decoration: line-through;
  color: #aaa;
}

.tasks-actions {
  display: flex;
  justify-content: flex-end;
  gap: $spacing-2-5;
  margin-top: 10px;
  flex-wrap: wrap;
}

.register-member-link {
  text-decoration: none;
}

.register-member-button {
  min-width: 180px;
}

.join-team-button {
  min-width: 140px;
}

.save-tasks-button {
  min-width: 120px;
}

@media (max-width: 768px) {
  .leads-list {
    grid-template-columns: 1fr;
  }
}


.coaching-text-inline {
  margin-top: 10px;
  padding: $spacing-3;
  background-color: #f0f8ff;
  border-left: 3px solid #007bff;
  @include typography-body;
  border-radius: $radius-sm;
  color: #555;
  line-height: 1.6;
  white-space: pre-line;
}

.lead-note-section {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid var(--vt-c-white-soft);
}

.note-header {
  margin-bottom: 10px;
}

.note-label {
  cursor: pointer;
  color: #555;
}

.note-label:hover {
  text-decoration: underline;
  color: #007bff;
}

.note-input-container {
  margin-top: 10px;
}

.note-loading {
  padding: $spacing-2-5;
  color: #666;
  font-style: italic;
}

.note-textarea {
  width: 100%;
  margin-bottom: 10px;
}

.note-actions {
  display: flex;
  justify-content: flex-end;
}

.save-note-button {
  min-width: 120px;
}
</style>
